import React, { useState, useEffect, useRef, useCallback } from "react";
import { useStats } from "./StatsContext";
import styles from "./KdfSelector.module.css";

const DEBOUNCE_MS = 300;

const KdfSelector = () => {
  const {
    kdfList,
    kdfListMeta,
    kdfSearchQuery,
    setKdfSearchQuery,
    loadKdfList,
    loadKdfStats,
    selectedKdfGroupId,
    isLoading: isStatsLoading,
  } = useStats();

  const [isOpen, setIsOpen] = useState(false);
  const [localSearch, setLocalSearch] = useState(kdfSearchQuery);
  const listRef = useRef(null);
  const searchRef = useRef(null);
  const debounceRef = useRef(null);

  // Load first page on mount
  useEffect(() => {
    loadKdfList(1, "");
  }, [loadKdfList]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchRef.current) {
      searchRef.current.focus();
    }
  }, [isOpen]);

  // Debounced search
  const handleSearchChange = useCallback(
    (e) => {
      const value = e.target.value;
      setLocalSearch(value);

      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        setKdfSearchQuery(value);
        loadKdfList(1, value);
      }, DEBOUNCE_MS);
    },
    [setKdfSearchQuery, loadKdfList]
  );

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    const el = listRef.current;
    if (!el) return;

    const { scrollTop, scrollHeight, clientHeight } = el;
    const nearBottom = scrollHeight - scrollTop - clientHeight < 50;

    if (nearBottom && kdfListMeta.hasNextPage && !kdfListMeta.isLoading) {
      loadKdfList(kdfListMeta.currentPage + 1, localSearch);
    }
  }, [kdfListMeta, loadKdfList, localSearch]);

  // Select a KDF
  const handleSelect = useCallback(
    (kdf) => {
      if (kdf.group_id === selectedKdfGroupId) {
        setIsOpen(false);
        return;
      }
      setIsOpen(false);
      loadKdfStats(kdf.group_id);
    },
    [selectedKdfGroupId, loadKdfStats]
  );

  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }
  }, [isOpen]);

  // Find the currently selected KDF for display
  const currentKdf = kdfList.find((k) => k.group_id === selectedKdfGroupId);
  const { doc } = window?.objInit || {};
  const displayTitle = currentKdf?.title || doc?.title || "Select a KDF";
  const displayFlag = currentKdf?.language_flag ||
    doc?.doc_files_languages?.find((l) => l.status === "active")?.language;

  return (
    <div className={styles.selectorWrapper}>
      {/* Trigger button */}
      <div
        className={`${styles.selectorTrigger} ${isOpen ? styles.selectorTriggerOpen : ""}`}
        onClick={() => setIsOpen(!isOpen)}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label="Select a KDF"
      >
        <div className={styles.triggerContent}>
          <span className={styles.triggerTitle}>{displayTitle}</span>
        </div>
        <span className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ""}`}>
          &#9660;
        </span>
      </div>

      {/* Dropdown panel */}
      {isOpen && (
        <>
          <div className={styles.overlay} onClick={() => setIsOpen(false)} />
          <div className={styles.dropdownPanel} role="listbox">
            {/* Search */}
            <div className={styles.searchWrapper}>
              <input
                ref={searchRef}
                type="text"
                className={styles.searchInput}
                placeholder="Search KDFs..."
                value={localSearch}
                onChange={handleSearchChange}
                aria-label="Search KDFs"
              />
            </div>

            {/* List */}
            <div
              ref={listRef}
              className={styles.listContainer}
              onScroll={handleScroll}
            >
              {/* Initial loading */}
              {kdfListMeta.isLoading && kdfList.length === 0 && (
                <div className={styles.loadingRow}>
                  <div className={styles.spinner}></div>
                  <span>Loading KDFs...</span>
                </div>
              )}

              {/* Empty state */}
              {!kdfListMeta.isLoading && kdfList.length === 0 && (
                <div className={styles.emptyState}>
                  {localSearch
                    ? "No matching KDFs found"
                    : "No KDFs with statistics found"}
                </div>
              )}

              {/* KDF items */}
              {kdfList.map((kdf) => (
                <div
                  key={`${kdf.group_id}-${kdf.language}`}
                  className={`${styles.kdfItem} ${
                    kdf.group_id === selectedKdfGroupId
                      ? styles.kdfItemActive
                      : ""
                  }`}
                  onClick={() => handleSelect(kdf)}
                  role="option"
                  aria-selected={kdf.group_id === selectedKdfGroupId}
                >
                  <div className={styles.kdfItemInfo}>
                    <div className={styles.kdfItemTitle}>{kdf.title}</div>
                    <div className={styles.kdfItemMeta}>
                      {" \u00B7 "}
                      {kdf.num_pages} pages
                    </div>
                  </div>
                  <div className={styles.kdfItemVisits}>
                    {kdf.visits_count} visits
                  </div>
                </div>
              ))}

              {/* Loading more (infinite scroll) */}
              {kdfListMeta.isLoading && kdfList.length > 0 && (
                <div className={styles.loadingRow}>
                  <div className={styles.spinner}></div>
                  <span>Loading more...</span>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Loading overlay for stats */}
      {isStatsLoading && (
        <div className={styles.loadingRow} style={{ marginTop: "4px" }}>
          <div className={styles.spinner}></div>
          <span>Loading statistics...</span>
        </div>
      )}
    </div>
  );
};

export default KdfSelector;
